// Slash command: "/" 入力で挿入メニュー (block_commands.js を共有)

import {
  $getSelection, $isRangeSelection,
  KEY_DOWN_COMMAND, COMMAND_PRIORITY_LOW,
} from "lexical"
import tippy from "tippy.js"

import { ALL_COMMANDS } from "./block_commands"

function filterCommands(query) {
  if (!query) return ALL_COMMANDS
  const q = query.toLowerCase()
  return ALL_COMMANDS.filter(c =>
    c.title.toLowerCase().includes(q) ||
    c.aliases.some(a => a.toLowerCase().includes(q))
  )
}

function detectTrigger(editor) {
  let trigger = null
  editor.read(() => {
    const sel = $getSelection()
    if (!$isRangeSelection(sel) || !sel.isCollapsed()) return
    const anchor = sel.anchor
    const node = anchor.getNode()
    if (node.getType() !== "text") return
    const text = node.getTextContent()
    const upTo = text.slice(0, anchor.offset)
    const m = upTo.match(/(?:^|\s)\/(\w*)$/)
    if (m) {
      trigger = {
        query: m[1],
        startOffset: anchor.offset - m[0].trimStart().length,
        node, anchor,
      }
    }
  })
  return trigger
}

function getCaretRect(editor) {
  const root = editor.getRootElement()
  if (!root) return null
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return null
  const range = sel.getRangeAt(0).cloneRange()
  range.collapse(true)
  const rect = range.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) {
    return root.getBoundingClientRect()
  }
  return rect
}

export function attachSlashMenu(editor) {
  let popup, container
  let items = []
  let selectedIndex = 0
  let activeTrigger = null

  const render = () => {
    if (!container) return
    container.innerHTML = ""
    if (items.length === 0) {
      const empty = document.createElement("div")
      empty.className = "px-3 py-2 text-sm text-akebono-text-muted"
      empty.textContent = "見つかりません"
      container.appendChild(empty)
      return
    }
    items.forEach((item, i) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = [
        "w-full text-left px-3 py-2 flex items-center gap-3",
        i === selectedIndex ? "bg-akebono-active" : "hover:bg-akebono-hover"
      ].join(" ")
      btn.innerHTML = `
        <span class="w-7 h-7 inline-flex items-center justify-center rounded bg-akebono-hover text-akebono-text-muted shrink-0 font-mono text-sm">${item.icon}</span>
        <span class="flex-1 min-w-0">
          <span class="block text-sm font-medium text-akebono-text">${item.title}</span>
          <span class="block text-xs text-akebono-text-muted truncate">${item.desc}</span>
        </span>
      `
      btn.addEventListener("mousedown", (e) => { e.preventDefault(); execute(item) })
      btn.addEventListener("mouseenter", () => { selectedIndex = i; render() })
      container.appendChild(btn)
    })
  }

  const execute = (item) => {
    if (activeTrigger) {
      editor.update(() => {
        const sel = $getSelection()
        if ($isRangeSelection(sel)) {
          const node = activeTrigger.node
          const offset = activeTrigger.anchor.offset
          const startOffset = activeTrigger.startOffset
          const t = node.getTextContent()
          node.setTextContent(t.slice(0, startOffset) + t.slice(offset))
          sel.setTextNodeRange(node, startOffset, node, startOffset)
        }
      })
    }
    item.run(editor)
    hide()
  }

  const show = (rect) => {
    if (!container) {
      container = document.createElement("div")
      container.className = "akebono-menu-popup bg-akebono-surface border border-akebono-border rounded-lg shadow-lg overflow-y-auto py-1 w-72 max-h-80"
    }
    if (!popup) {
      popup = tippy("body", {
        getReferenceClientRect: () => rect,
        appendTo: () => document.body,
        content: container,
        showOnCreate: true,
        interactive: true,
        trigger: "manual",
        placement: "bottom-start",
        theme: "akebono",
      })[0]
    } else {
      popup.setProps({ getReferenceClientRect: () => rect })
      popup.show()
    }
    render()
  }

  const hide = () => {
    if (popup) popup.hide()
    activeTrigger = null
    selectedIndex = 0
  }

  const unregister = editor.registerUpdateListener(({ editorState }) => {
    editorState.read(() => {
      const trigger = detectTrigger(editor)
      if (trigger) {
        activeTrigger = trigger
        items = filterCommands(trigger.query)
        selectedIndex = 0
        const rect = getCaretRect(editor)
        if (rect) show(rect)
      } else {
        hide()
      }
    })
  })

  const removeKey = editor.registerCommand(
    KEY_DOWN_COMMAND,
    (event) => {
      if (!popup || !popup.state.isVisible) return false
      if (event.key === "ArrowDown") {
        event.preventDefault()
        selectedIndex = (selectedIndex + 1) % Math.max(1, items.length)
        render()
        return true
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        selectedIndex = (selectedIndex - 1 + items.length) % Math.max(1, items.length)
        render()
        return true
      }
      if (event.key === "Enter") {
        if (items[selectedIndex]) {
          event.preventDefault()
          execute(items[selectedIndex])
          return true
        }
      }
      if (event.key === "Escape") {
        event.preventDefault()
        hide()
        return true
      }
      return false
    },
    COMMAND_PRIORITY_LOW,
  )

  return () => {
    unregister()
    removeKey()
    if (popup) popup.destroy()
  }
}
